import { 
  Sparkles, 
  Layers, 
  RefreshCw, 
  History,
  Zap,
  Shield,
  Factory
} from "lucide-react";
import { useTranslation } from "@/i18n";

const Features = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: Sparkles,
      titleKey: "features.list.complete.title",
      descriptionKey: "features.list.complete.description",
      size: "large",
      gradient: "from-primary/20 to-accent/20",
    },
    {
      icon: Layers,
      titleKey: "features.list.bulk.title",
      descriptionKey: "features.list.bulk.description",
      size: "normal",
      gradient: "from-accent/20 to-primary/20",
    },
    {
      icon: RefreshCw,
      titleKey: "features.list.updated.title",
      descriptionKey: "features.list.updated.description",
      size: "normal",
      gradient: "from-primary/20 to-accent/10",
    },
    {
      icon: History,
      titleKey: "features.list.history.title",
      descriptionKey: "features.list.history.description",
      size: "normal",
      gradient: "from-accent/10 to-primary/20",
    },
    {
      icon: Shield,
      titleKey: "features.list.antiCannibalization.title",
      descriptionKey: "features.list.antiCannibalization.description",
      size: "normal",
      gradient: "from-primary/15 to-accent/25",
    },
    {
      icon: Factory,
      titleKey: "features.list.industryProfiles.title",
      descriptionKey: "features.list.industryProfiles.description",
      size: "normal",
      gradient: "from-accent/25 to-primary/15",
    },
  ];

  return (
    <section id="features" className="py-20 md:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="glow-orb glow-orb-primary w-[500px] h-[500px] top-20 -left-40 opacity-20" />
        <div className="glow-orb glow-orb-accent w-[400px] h-[400px] bottom-20 right-0 opacity-15" />
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-sm font-medium mb-6">
            <Zap size={16} className="text-primary" />
            <span className="text-foreground">{t('features.badge')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t('features.title')}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t('features.subtitle')}
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`group p-6 md:p-8 rounded-2xl glass-card border border-border/30 hover-lift transition-all duration-300 ${
                index === 0 ? 'lg:col-span-2 lg:row-span-1' : ''
              }`}
            >
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-5 transition-transform group-hover:scale-110`}>
                <feature.icon className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-3">
                {t(feature.titleKey)}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {t(feature.descriptionKey)}
              </p>
            </div>
          ))}
        </div>

        {/* Testimonial */}
        <div className="mt-16 max-w-3xl mx-auto">
          <div className="p-8 rounded-2xl glass-card gradient-border text-center">
            <p className="text-lg md:text-xl text-foreground mb-4 italic">
              "{t('features.testimonial.quote')}"
            </p>
            <p className="text-muted-foreground">
              — <span className="font-semibold text-foreground">{t('features.testimonial.author')}</span>, {t('features.testimonial.role')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
